import React, { useState, useEffect } from 'react';
import UserGroup from '../icons/UserGroup';
import { apiClient } from '../../utils/apiClient';

interface AccessRequestPromptProps {
  dossierId: string;
  dossierTitle: string;
  dossierDescription: string;
}

const AccessRequestPrompt: React.FC<AccessRequestPromptProps> = ({
  dossierId,
  dossierTitle,
  dossierDescription,
}) => {
  const [status, setStatus] = useState<'idle' | 'pending' | 'rejected' | 'sending' | 'sent'>('idle');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await apiClient(`/access-requests/${dossierId}/status`);
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'pending') setStatus('pending');
          else if (data.status === 'rejected') setStatus('rejected');
        }
      } catch (err) {
        console.error('Failed to check access status:', err);
      }
    };
    checkStatus();
  }, [dossierId]);

  const handleRequestAccess = async () => {
    setError('');
    setStatus('sending');
    try {
      const response = await apiClient(`/access-requests/${dossierId}`, {
        method: 'POST',
        body: JSON.stringify({ message: message || undefined }),
      });

      if (response.ok) {
        setStatus('sent');
      } else {
        const data = await response.json();
        setError(data.message || 'Erreur lors de l\'envoi de la demande');
        setStatus('idle');
      }
    } catch (err) {
      setError('Erreur réseau');
      setStatus('idle');
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-mcdm-primary-100 dark:bg-mcdm-third-500">
      <div className="max-w-lg w-full mx-4">
        <div className="bg-white dark:bg-mcdm-third-400 rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-mcdm-primary-200 dark:bg-mcdm-third-300 rounded-full flex items-center justify-center">
              <UserGroup width="32px" height="32px" fill="currentColor" className="text-mcdm-third-400 dark:text-mcdm-primary-100" />
            </div>
          </div>

          <h1 className="text-xl font-platypi text-center text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2">
            {dossierTitle}
          </h1>

          {dossierDescription && (
            <p className="text-sm text-center text-mcdm-third-300 dark:text-mcdm-primary-300 mb-6">
              {dossierDescription}
            </p>
          )}

          <div className="border-t border-mcdm-primary-200 dark:border-mcdm-third-300 my-6" />

          {/* Statuts */}
          {(status === 'pending' || status === 'sent') && (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-lg text-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Votre demande d'accès est en attente de validation
              </div>
            </div>
          )}

          {status === 'rejected' && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Votre précédente demande a été refusée
                </div>
              </div>
              <p className="text-center text-sm text-mcdm-third-300 dark:text-mcdm-primary-300">
                Vous pouvez renvoyer une demande si vous le souhaitez.
              </p>
              <button
                onClick={handleRequestAccess}
                className="w-full py-2.5 bg-mcdm-third-500 dark:bg-mcdm-primary-200 text-white dark:text-mcdm-third-500 rounded-lg text-sm font-medium hover:bg-mcdm-third-600 dark:hover:bg-white transition-colors"
              >
                Renvoyer une demande
              </button>
            </div>
          )}

          {status === 'idle' && (
            <div className="space-y-4">
              <p className="text-center text-sm text-mcdm-third-400 dark:text-mcdm-primary-200">
                Ce dossier est accessible sur demande. Envoyez une demande d'accès à l'auteur.
              </p>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Message optionnel pour l'auteur..."
                className="w-full p-3 rounded-lg border border-mcdm-primary-200 dark:border-mcdm-third-300 bg-white dark:bg-mcdm-third-500 text-sm text-mcdm-third-500 dark:text-mcdm-primary-100 placeholder-mcdm-third-300 dark:placeholder-mcdm-primary-300 focus:ring-2 focus:ring-mcdm-secondary-400 focus:border-transparent outline-none resize-none"
                rows={3}
                maxLength={500}
              />
              <button
                onClick={handleRequestAccess}
                className="w-full py-2.5 bg-mcdm-third-500 dark:bg-mcdm-primary-200 text-white dark:text-mcdm-third-500 rounded-lg text-sm font-medium hover:bg-mcdm-third-600 dark:hover:bg-white transition-colors"
              >
                Demander l'accès
              </button>
            </div>
          )}

          {status === 'sending' && (
            <div className="text-center py-4">
              <div className="inline-block w-6 h-6 border-2 border-mcdm-third-300 border-t-mcdm-third-500 rounded-full animate-spin" />
            </div>
          )}

          {error && (
            <p className="text-center text-sm text-red-500 mt-3">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessRequestPrompt;
